import { Column, HeaderFilter } from 'devextreme-react/data-grid'
import { DataSourceOptions } from 'devextreme/data/data_source'
import ODataStore from 'devextreme/data/odata/store'
import { Store } from 'devextreme/data/store'
// eslint-disable-next-line import/no-namespace
import type * as LocalizationTypes from 'devextreme/localization'

const nullDate = '0001-01-01T00:00:00.000Z'

const getDateValue = (value: unknown): Date | null => {
  if (value === undefined || value === null || value === '') return null

  const date = value instanceof Date ? value : new Date(value as string | number)
  if (Number.isNaN(date.getTime())) return null
  if (date.toISOString() === nullDate) return null

  return date
}

const getNextDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0)

const getDateFilterValue = (date: Date): Date => new Date(date.toISOString().replace('Z', ''))

const DateColumn = (props: {
  dataField?: string
  caption?: string
  format?: LocalizationTypes.Format
  width?: string | number
  visible?: boolean
  allowSorting?: boolean
  defaultSortOrder?: 'asc' | 'desc'
  dataSourceHeaderFilter?:
    | any[]
    | Store<any, any>
    | DataSourceOptions<any, any, any, any>
    | ((options: { component: Record<string, any>; dataSource: DataSourceOptions | null }) => void)
    | null
  hidingPriority?: number
}) => {
  const {
    dataField,
    caption,
    format,
    width,
    defaultSortOrder,
    visible,
    allowSorting,
    dataSourceHeaderFilter,
    hidingPriority,
  } = props

  const CreateDataGridDateUTCFilter = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
    dataField: string | undefined,
  ): any => {
    let startDate: Date | null = null
    let endDate: Date | null = null
    switch (selectedFilterOperation) {
      case 'between': {
        if (!Array.isArray(filterValue)) return null

        startDate = getDateValue(filterValue[0])
        const endFilterValue = getDateValue(filterValue[1])
        endDate = endFilterValue ? getNextDay(endFilterValue) : null

        break
      }
      case '=':
      case '<>': {
        startDate = getDateValue(filterValue)
        endDate = startDate ? getNextDay(startDate) : null
        break
      }
      case '>':
      case '<=': {
        const date = getDateValue(filterValue)
        startDate = date ? getNextDay(date) : null
        break
      }
      default: {
        startDate = getDateValue(filterValue)
      }
    }

    if (!startDate && !endDate) return null

    let endDateFilterValue: Date | null = null
    if (endDate) {
      endDateFilterValue = getDateFilterValue(endDate)
    }
    const startDateFilterValue = startDate ? getDateFilterValue(startDate) : null
    if (target === 'filterBuilder' || target === 'filterRow') {
      switch (selectedFilterOperation) {
        case '=': {
          if (!startDateFilterValue || !endDateFilterValue) return null

          return [[dataField, '>=', startDateFilterValue], 'and', [dataField, '<', endDateFilterValue]]
        }
        case 'between': {
          if (startDateFilterValue && endDateFilterValue)
            return [[dataField, '>=', startDateFilterValue], 'and', [dataField, '<', endDateFilterValue]]
          if (startDateFilterValue) return [[dataField, '>=', startDateFilterValue]]
          if (endDateFilterValue) return [[dataField, '<', endDateFilterValue]]

          return null
        }
        case '<>': {
          if (!startDateFilterValue || !endDateFilterValue) return null

          return [[dataField, '<', startDateFilterValue], 'or', [dataField, '>=', endDateFilterValue]]
        }
        case '>':
        case '<=':
        default: {
          if (!startDateFilterValue) return null
        }
      }

      if (selectedFilterOperation === '>') {
        return [[dataField, '>=', startDateFilterValue]]
      }
      if (selectedFilterOperation === '<=') {
        return [[dataField, '<', startDateFilterValue]]
      }

      return [[dataField, selectedFilterOperation, startDateFilterValue]]
    } else if (target === 'search') {
      if (!startDate || !startDateFilterValue) return null

      endDate = getNextDay(startDate)
      endDateFilterValue = getDateFilterValue(endDate)
      return [[dataField, '>=', startDateFilterValue], 'and', [dataField, '<', endDateFilterValue]]
    }
    return filterValue
  }

  const calculateDateUTCFilterExpression = (
    filterValue: any,
    selectedFilterOperation: string | null,
    target: string,
  ): any => {
    return CreateDataGridDateUTCFilter(filterValue, selectedFilterOperation, target, dataField)
  }

  const calculateCellValue = (rowData: any) => {
    let value: unknown = null
    if (dataField?.includes('.')) {
      const partsOfDataField = dataField.split('.')
      value = partsOfDataField.reduce((currentValue: any, part) => currentValue?.[part], rowData)
    } else {
      value = rowData[`${dataField}`]
    }
    return getDateValue(value)
  }
  return (
    <Column
      dataField={dataField}
      caption={caption}
      format={format}
      width={width}
      defaultSortOrder={defaultSortOrder}
      visible={visible}
      allowSorting={allowSorting}
      allowHeaderFiltering={false}
      dataType="date"
      calculateCellValue={calculateCellValue}
      calculateFilterExpression={calculateDateUTCFilterExpression}
      hidingPriority={hidingPriority}
    >
      {dataSourceHeaderFilter && <HeaderFilter dataSource={dataSourceHeaderFilter} />}
    </Column>
  )
}

export default DateColumn
